import { IMAGES } from '@/assets/images';
import type {
  Service,
  Industry,
  FleetItem,
  TeamMember,
  ServiceArea,
  NewsArticle,
  Career,
} from '@/lib';

export const services: Service[] = [
  {
    id: 'truck-parts',
    title: 'Heavy Duty Truck Parts',
    description: 'Comprehensive inventory of heavy-duty truck parts from leading manufacturers. We supply quality components for all major truck brands, ensuring your fleet stays operational with minimal downtime.',
    icon: 'Truck',
    features: [
      'Extensive parts catalog for all major truck brands',
      'OEM and aftermarket options available',
      'Same-day delivery across Nassau',
      'Expert technical support and consultation',
      'Warranty on all parts',
      'Emergency parts sourcing service',
    ],
    benefits: [
      'Reduced vehicle downtime',
      'Competitive pricing on bulk orders',
      'Quality assurance on all components',
      'Island-wide delivery network',
    ],
    image: IMAGES.TRUCK_LOGISTICS_1,
  },
  {
    id: 'procurement',
    title: 'Procurement Services',
    description: 'End-to-end procurement solutions for businesses across the Bahamas. We manage your supply chain from vendor sourcing to final delivery, ensuring cost-effective and timely acquisition of materials and equipment.',
    icon: 'Package',
    features: [
      'Vendor network management',
      'International sourcing capabilities',
      'Customs clearance coordination',
      'Inventory management solutions',
      'Cost optimization strategies',
      'Quality control and inspection',
    ],
    benefits: [
      'Streamlined supply chain operations',
      'Access to global supplier network',
      'Reduced procurement costs',
      'Simplified logistics coordination',
    ],
    image: IMAGES.PORT_OPERATIONS_3,
  },
  {
    id: 'barge-operations',
    title: 'Barge Operations',
    description: 'Professional marine transportation services for cargo and equipment across the Bahamas. Our modern barge fleet handles inter-island delivery, construction materials, heavy equipment, and bulk cargo with safety and efficiency.',
    icon: 'Ship',
    features: [
      'Modern barge fleet for various cargo types',
      'Inter-island transportation network',
      'Heavy equipment and oversized cargo handling',
      'Construction materials delivery',
      'Scheduled and charter services',
      'Marine cargo insurance available',
    ],
    benefits: [
      'Reliable inter-island logistics',
      'Experienced marine crew',
      'Flexible scheduling options',
      'Comprehensive safety protocols',
    ],
    image: IMAGES.BARGE_OPERATIONS_2,
  },
  {
    id: 'waste-management',
    title: 'Waste Removal & Management',
    description: 'Comprehensive waste collection, removal, and disposal services for commercial, industrial, and government clients. We provide environmentally responsible waste management solutions with full regulatory compliance.',
    icon: 'Trash2',
    features: [
      'Commercial and industrial waste collection',
      'Hazardous waste handling and disposal',
      'Recycling program management',
      'Construction debris removal',
      'Scheduled pickup services',
      'Environmental compliance reporting',
    ],
    benefits: [
      'Full regulatory compliance',
      'Environmentally responsible disposal',
      'Flexible service schedules',
      'Certified waste handling procedures',
    ],
    image: IMAGES.WASTE_MANAGEMENT_2,
  },
];

export const industries: Industry[] = [
  {
    id: 'construction',
    name: 'Construction & Infrastructure',
    description: 'Supporting major construction projects across the Bahamas with logistics, equipment, materials delivery, and waste management services. From residential developments to large-scale infrastructure projects.',
    services: [
      'Heavy equipment transportation',
      'Construction materials delivery',
      'Site waste management',
      'Equipment parts supply',
      'Inter-island logistics coordination',
    ],
    caseStudy: {
      title: 'Nassau Harbor Infrastructure Project',
      description: 'Provided comprehensive logistics support for a major harbor expansion project, including barge operations for heavy equipment delivery and ongoing waste management services.',
      results: [
        'Delivered 50+ heavy equipment units via barge',
        'Managed 2,000+ tons of construction waste',
        'Zero safety incidents over 18-month project',
        'On-time delivery rate of 98%',
      ],
    },
    image: IMAGES.CONSTRUCTION_1,
  },
  {
    id: 'renewable-energy',
    name: 'Renewable Energy',
    description: 'Specialized logistics and procurement services for solar, wind, and other renewable energy projects. We understand the unique requirements of clean energy installations and provide tailored solutions.',
    services: [
      'Solar panel and equipment procurement',
      'Specialized transportation for sensitive equipment',
      'Inter-island delivery for remote installations',
      'Technical equipment sourcing',
      'Project logistics coordination',
    ],
    caseStudy: {
      title: 'Family Islands Solar Initiative',
      description: 'Coordinated procurement and delivery of solar equipment for multiple family island installations, managing complex logistics across 8 islands.',
      results: [
        'Sourced and delivered equipment for 12 solar installations',
        'Coordinated logistics across 8 islands',
        'Reduced procurement costs by 22%',
        'Completed all deliveries within project timeline',
      ],
    },
    image: IMAGES.RENEWABLE_ENERGY_2,
  },
  {
    id: 'government',
    name: 'Government & Municipal',
    description: 'Trusted partner for government agencies and municipal services across the Bahamas. We provide reliable waste management, fleet maintenance, and procurement services for public sector operations.',
    services: [
      'Municipal waste collection and management',
      'Government fleet parts supply',
      'Public works equipment procurement',
      'Emergency response logistics',
      'Inter-island government cargo transport',
    ],
    image: IMAGES.BAHAMAS_2,
  },
  {
    id: 'maritime',
    name: 'Maritime & Port Operations',
    description: 'Supporting maritime industry with barge operations, port logistics, marine equipment supply, and cargo handling services. We serve shipping companies, port authorities, and marine contractors.',
    services: [
      'Port-to-port cargo transportation',
      'Marine equipment and parts supply',
      'Barge charter services',
      'Cargo handling and stevedoring support',
      'Marine waste management',
    ],
    caseStudy: {
      title: 'Grand Bahama Port Authority Partnership',
      description: 'Long-term partnership providing barge operations and logistics support for port expansion and ongoing operations.',
      results: [
        'Transported 500+ containers via barge',
        'Maintained 99.5% on-time delivery rate',
        'Supported port operations for 3+ years',
        'Zero cargo damage incidents',
      ],
    },
    image: IMAGES.PORT_OPERATIONS_5,
  },
  {
    id: 'commercial',
    name: 'Commercial & Retail',
    description: 'Logistics and procurement solutions for commercial businesses and retail operations. From inventory sourcing to waste management, we support business operations across the islands.',
    services: [
      'Commercial goods procurement',
      'Retail inventory logistics',
      'Commercial waste management',
      'Inter-island delivery services',
      'Supply chain optimization',
    ],
    image: IMAGES.BAHAMAS_2,
  },
];

export const fleetCategories = [
  {
    id: 'trucks-trailers',
    name: 'Trucks & Trailers',
    description: 'Modern fleet of heavy-duty trucks and specialized trailers for cargo transport and logistics operations.',
    items: [
      {
        id: 'truck-1',
        name: 'Freightliner Cascadia Heavy Duty Truck',
        category: 'trucks-trailers' as const,
        specifications: {
          'Engine': 'Detroit DD15 500HP',
          'Transmission': 'Automated Manual',
          'Capacity': '80,000 lbs GVWR',
          'Year': '2024',
        },
        availability: 'available' as const,
        image: IMAGES.TRUCK_LOGISTICS_1,
      },
      {
        id: 'truck-2',
        name: 'Kenworth T680 Day Cab',
        category: 'trucks-trailers' as const,
        specifications: {
          'Engine': 'Paccar MX-13 455HP',
          'Transmission': '10-Speed Automated',
          'Capacity': '80,000 lbs GVWR',
          'Year': '2023',
        },
        availability: 'in-use' as const,
        image: IMAGES.TRUCK_LOGISTICS_1,
      },
      {
        id: 'trailer-1',
        name: '53ft Dry Van Trailer',
        category: 'trucks-trailers' as const,
        specifications: {
          'Length': '53 feet',
          'Width': '102 inches',
          'Height': '13.5 feet',
          'Capacity': '45,000 lbs',
        },
        availability: 'available' as const,
        image: IMAGES.TRUCK_LOGISTICS_1,
      },
      {
        id: 'trailer-2',
        name: 'Flatbed Trailer 48ft',
        category: 'trucks-trailers' as const,
        specifications: {
          'Length': '48 feet',
          'Width': '102 inches',
          'Capacity': '48,000 lbs',
          'Type': 'Steel deck',
        },
        availability: 'available' as const,
        image: IMAGES.TRUCK_LOGISTICS_1,
      },
    ],
  },
  {
    id: 'barges-marine',
    name: 'Barges & Marine Equipment',
    description: 'Professional barge fleet for inter-island cargo transport and marine operations across the Bahamas.',
    items: [
      {
        id: 'barge-1',
        name: 'Cargo Barge 120ft',
        category: 'barges-marine' as const,
        specifications: {
          'Length': '120 feet',
          'Width': '35 feet',
          'Draft': '6 feet',
          'Capacity': '500 tons',
        },
        availability: 'available' as const,
        image: IMAGES.BARGE_OPERATIONS_2,
      },
      {
        id: 'barge-2',
        name: 'Heavy Equipment Barge 150ft',
        category: 'barges-marine' as const,
        specifications: {
          'Length': '150 feet',
          'Width': '40 feet',
          'Draft': '8 feet',
          'Capacity': '800 tons',
        },
        availability: 'in-use' as const,
        image: IMAGES.BARGE_OPERATIONS_2,
      },
      {
        id: 'tugboat-1',
        name: 'Harbor Tugboat 65ft',
        category: 'barges-marine' as const,
        specifications: {
          'Length': '65 feet',
          'Engine': 'Twin Diesel 1200HP',
          'Bollard Pull': '25 tons',
          'Year': '2022',
        },
        availability: 'available' as const,
        image: IMAGES.BARGE_OPERATIONS_2,
      },
    ],
  },
  {
    id: 'heavy-equipment',
    name: 'Heavy Equipment',
    description: 'Construction and industrial equipment for various project requirements and operations.',
    items: [
      {
        id: 'excavator-1',
        name: 'Caterpillar 336 Excavator',
        category: 'heavy-equipment' as const,
        specifications: {
          'Operating Weight': '36,000 kg',
          'Engine': 'Cat C9.3B 302HP',
          'Bucket Capacity': '1.9 m³',
          'Year': '2023',
        },
        availability: 'available' as const,
        image: IMAGES.CONSTRUCTION_1,
      },
      {
        id: 'loader-1',
        name: 'John Deere 644P Wheel Loader',
        category: 'heavy-equipment' as const,
        specifications: {
          'Operating Weight': '24,000 kg',
          'Engine': 'John Deere 9.0L 243HP',
          'Bucket Capacity': '3.8 m³',
          'Year': '2024',
        },
        availability: 'maintenance' as const,
        image: IMAGES.CONSTRUCTION_1,
      },
      {
        id: 'forklift-1',
        name: 'Toyota 8FGU25 Forklift',
        category: 'heavy-equipment' as const,
        specifications: {
          'Capacity': '5,000 lbs',
          'Lift Height': '189 inches',
          'Fuel Type': 'LPG',
          'Year': '2023',
        },
        availability: 'available' as const,
        image: IMAGES.CONSTRUCTION_1,
      },
    ],
  },
  {
    id: 'waste-management',
    name: 'Waste Management Fleet',
    description: 'Specialized vehicles and equipment for comprehensive waste collection and management services.',
    items: [
      {
        id: 'waste-truck-1',
        name: 'Mack LR Refuse Truck',
        category: 'waste-management' as const,
        specifications: {
          'Capacity': '25 cubic yards',
          'Compaction Ratio': '3:1',
          'Engine': 'Mack MP7 325HP',
          'Year': '2023',
        },
        availability: 'available' as const,
        image: IMAGES.WASTE_MANAGEMENT_2,
      },
      {
        id: 'waste-truck-2',
        name: 'Peterbilt 520 Front Loader',
        category: 'waste-management' as const,
        specifications: {
          'Capacity': '30 cubic yards',
          'Compaction Ratio': '4:1',
          'Engine': 'Cummins ISL9 380HP',
          'Year': '2024',
        },
        availability: 'in-use' as const,
        image: IMAGES.WASTE_MANAGEMENT_2,
      },
      {
        id: 'roll-off-1',
        name: 'Roll-Off Container Truck',
        category: 'waste-management' as const,
        specifications: {
          'Container Size': '20-40 yards',
          'Hoist Capacity': '50,000 lbs',
          'Engine': 'Detroit DD13 450HP',
          'Year': '2023',
        },
        availability: 'available' as const,
        image: IMAGES.WASTE_MANAGEMENT_2,
      },
    ],
  },
];

export const serviceAreas: ServiceArea[] = [
  {
    id: 'nassau',
    name: 'Nassau / New Providence',
    servicesAvailable: [
      'Heavy Duty Truck Parts',
      'Procurement Services',
      'Barge Operations',
      'Waste Removal & Management',
    ],
    contactInfo: {
      phone: '+1 (242) 555-0100',
      email: 'nassau@clhservices.bs',
    },
    keyFacilities: [
      'Main headquarters and operations center',
      'Parts warehouse and distribution center',
      'Barge terminal and marine operations',
      'Fleet maintenance facility',
      'Waste management depot',
    ],
    image: IMAGES.BAHAMAS_2,
  },
  {
    id: 'grand-bahama',
    name: 'Grand Bahama',
    servicesAvailable: [
      'Heavy Duty Truck Parts',
      'Procurement Services',
      'Barge Operations',
      'Waste Removal & Management',
    ],
    contactInfo: {
      phone: '+1 (242) 555-0200',
      email: 'freeport@clhservices.bs',
    },
    keyFacilities: [
      'Regional operations center',
      'Parts distribution hub',
      'Port logistics coordination',
      'Waste collection services',
    ],
    image: IMAGES.BAHAMAS_2,
  },
  {
    id: 'abaco',
    name: 'Abaco',
    servicesAvailable: [
      'Procurement Services',
      'Barge Operations',
      'Waste Removal & Management',
    ],
    contactInfo: {
      phone: '+1 (242) 555-0300',
      email: 'abaco@clhservices.bs',
    },
    keyFacilities: [
      'Barge terminal',
      'Procurement coordination office',
      'Waste collection depot',
    ],
    image: IMAGES.BAHAMAS_2,
  },
  {
    id: 'eleuthera',
    name: 'Eleuthera',
    servicesAvailable: [
      'Procurement Services',
      'Barge Operations',
      'Waste Removal & Management',
    ],
    contactInfo: {
      phone: '+1 (242) 555-0400',
      email: 'eleuthera@clhservices.bs',
    },
    keyFacilities: [
      'Inter-island logistics hub',
      'Waste management services',
    ],
    image: IMAGES.BAHAMAS_2,
  },
  {
    id: 'exuma',
    name: 'Exuma',
    servicesAvailable: [
      'Procurement Services',
      'Barge Operations',
      'Waste Removal & Management',
    ],
    contactInfo: {
      phone: '+1 (242) 555-0500',
      email: 'exuma@clhservices.bs',
    },
    keyFacilities: [
      'Barge operations',
      'Procurement services',
      'Waste collection',
    ],
    image: IMAGES.BAHAMAS_2,
  },
  {
    id: 'family-islands',
    name: 'Family Islands',
    servicesAvailable: [
      'Procurement Services',
      'Barge Operations',
    ],
    contactInfo: {
      phone: '+1 (242) 555-0600',
      email: 'familyislands@clhservices.bs',
    },
    keyFacilities: [
      'Scheduled barge service',
      'Procurement coordination',
    ],
    image: IMAGES.BAHAMAS_2,
  },
];

export const teamMembers: TeamMember[] = [
  {
    id: 'ceo',
    name: 'Christopher Lewis',
    position: 'Chief Executive Officer',
    bio: 'With over 25 years of experience in logistics and marine operations, Christopher leads C.L. & H Services with a vision for comprehensive, reliable service across the Bahamas. His expertise in supply chain management and commitment to safety has positioned the company as a trusted partner for major projects throughout the islands.',
    image: IMAGES.TEAM_1,
  },
  {
    id: 'coo',
    name: 'Helen Thompson',
    position: 'Chief Operations Officer',
    bio: 'Helen brings 20 years of operational excellence to C.L. & H Services, overseeing all service divisions including barge operations, waste management, and fleet coordination. Her focus on efficiency and customer satisfaction ensures seamless execution of complex logistics projects.',
    image: IMAGES.TEAM_1,
  },
  {
    id: 'marine-director',
    name: 'Marcus Johnson',
    position: 'Director of Marine Operations',
    bio: 'A licensed master mariner with 30 years of experience in Caribbean waters, Marcus manages all barge operations and marine logistics. His deep knowledge of inter-island navigation and cargo handling ensures safe, efficient transportation across the Bahamas.',
    image: IMAGES.TEAM_1,
  },
  {
    id: 'procurement-director',
    name: 'Sarah Williams',
    position: 'Director of Procurement Services',
    bio: 'Sarah leads our procurement division with expertise in international sourcing, vendor management, and supply chain optimization. Her strategic approach has helped clients reduce costs while maintaining quality and reliability in their supply chains.',
    image: IMAGES.TEAM_1,
  },
  {
    id: 'fleet-manager',
    name: 'David Brown',
    position: 'Fleet & Equipment Manager',
    bio: 'David oversees our extensive fleet of trucks, trailers, and heavy equipment, ensuring optimal performance and availability. His background in heavy-duty mechanics and fleet management keeps our operations running smoothly with minimal downtime.',
    image: IMAGES.TEAM_1,
  },
  {
    id: 'safety-director',
    name: 'Jennifer Davis',
    position: 'Director of Safety & Compliance',
    bio: 'Jennifer ensures all operations meet or exceed safety standards and regulatory requirements. Her comprehensive approach to safety training and compliance has resulted in an exemplary safety record across all service divisions.',
    image: IMAGES.TEAM_1,
  },
];

export const certifications = [
  {
    id: 'iso-9001',
    name: 'ISO 9001:2015',
    description: 'Quality Management System Certification',
    issuer: 'International Organization for Standardization',
  },
  {
    id: 'maritime-license',
    name: 'Bahamas Maritime Authority License',
    description: 'Licensed for commercial marine operations',
    issuer: 'Bahamas Maritime Authority',
  },
  {
    id: 'waste-permit',
    name: 'Waste Management Permit',
    description: 'Authorized for commercial waste collection and disposal',
    issuer: 'Ministry of Environment & Housing',
  },
  {
    id: 'safety-cert',
    name: 'OSHA Safety Certification',
    description: 'Occupational safety and health compliance',
    issuer: 'Occupational Safety & Health Administration',
  },
];

export const newsArticles: NewsArticle[] = [
  {
    id: 'article-2',
    title: 'Supply Chain Resilience: Lessons from 2025 Hurricane Season',
    date: '2026-02-28',
    category: 'logistics-trends',
    excerpt: 'How advanced planning and diversified logistics networks helped maintain supply chains during the 2025 hurricane season.',
    content: 'The 2025 hurricane season tested supply chain resilience across the Caribbean. Companies with diversified logistics networks, pre-positioned inventory, and flexible transportation options maintained operations while others faced significant disruptions. This article explores key strategies for building resilient supply chains in hurricane-prone regions.',
    image: IMAGES.PORT_OPERATIONS_3,
    featured: true,
  },
  {
    id: 'article-3',
    title: 'The Future of Inter-Island Logistics in the Bahamas',
    date: '2026-02-10',
    category: 'industry-insights',
    excerpt: 'Emerging technologies and sustainable practices are transforming how goods move between islands in the Bahamas archipelago.',
    content: 'Inter-island logistics in the Bahamas is evolving with new technologies including GPS tracking, automated scheduling systems, and eco-friendly marine vessels. These innovations are reducing costs, improving reliability, and minimizing environmental impact while supporting economic development across the family islands.',
    image: IMAGES.BAHAMAS_2,
    featured: false,
  },
  {
    id: 'article-4',
    title: 'Waste Management Best Practices for Construction Projects',
    date: '2026-01-20',
    category: 'industry-insights',
    excerpt: 'Effective waste management strategies can reduce project costs and environmental impact while ensuring regulatory compliance.',
    content: 'Construction projects generate significant waste, but proper management can turn this challenge into an opportunity. This article covers best practices including waste segregation, recycling programs, hazardous material handling, and compliance with Bahamian environmental regulations.',
    image: IMAGES.WASTE_MANAGEMENT_2,
    featured: false,
  },
  {
    id: 'article-6',
    title: 'Optimizing Fleet Maintenance for Maximum Uptime',
    date: '2025-12-15',
    category: 'supply-chain',
    excerpt: 'Preventive maintenance strategies and parts inventory management are key to reducing vehicle downtime and operational costs.',
    content: 'Fleet downtime directly impacts operational efficiency and profitability. This article explores preventive maintenance schedules, predictive maintenance technologies, strategic parts inventory management, and training programs that keep commercial fleets running at peak performance.',
    image: IMAGES.TRUCK_LOGISTICS_1,
    featured: false,
  },
];

export const careers: Career[] = [
  {
    id: 'marine-captain',
    title: 'Barge Captain',
    location: 'Nassau, New Providence',
    type: 'full-time',
    description: 'Experienced marine captain needed to command barge operations for inter-island cargo transportation. Must hold valid Bahamas maritime license and have extensive knowledge of Bahamian waters.',
    requirements: [
      'Valid Bahamas Master Mariner License (minimum 500 ton)',
      '5+ years experience commanding commercial vessels',
      'Extensive knowledge of Bahamian waters and navigation',
      'Strong leadership and crew management skills',
      'Excellent safety record',
      'Radio operator certification',
    ],
    responsibilities: [
      'Command barge operations for inter-island cargo transport',
      'Ensure safe navigation and cargo handling',
      'Manage crew and coordinate with shore operations',
      'Maintain vessel logs and documentation',
      'Conduct pre-voyage safety inspections',
      'Coordinate with port authorities and customs',
    ],
    benefits: [
      'Competitive salary based on experience',
      'Health insurance coverage',
      'Paid time off and vacation',
      'Professional development opportunities',
      'Retirement plan',
    ],
  },
  {
    id: 'procurement-specialist',
    title: 'Procurement Specialist',
    location: 'Nassau, New Providence',
    type: 'full-time',
    description: 'Join our procurement team to manage vendor relationships, source materials and equipment, and optimize supply chain operations for clients across the Bahamas.',
    requirements: [
      'Bachelor degree in Supply Chain Management, Business, or related field',
      '3+ years experience in procurement or supply chain',
      'Strong negotiation and vendor management skills',
      'Proficiency in procurement software and MS Office',
      'Knowledge of international shipping and customs procedures',
      'Excellent communication and organizational skills',
    ],
    responsibilities: [
      'Source and procure materials, equipment, and services for clients',
      'Manage vendor relationships and negotiate contracts',
      'Coordinate international shipping and customs clearance',
      'Maintain procurement records and documentation',
      'Analyze costs and identify savings opportunities',
      'Ensure compliance with procurement policies',
    ],
    benefits: [
      'Competitive salary',
      'Health and dental insurance',
      'Professional development and training',
      'Paid time off',
      'Performance bonuses',
    ],
  },
  {
    id: 'diesel-mechanic',
    title: 'Heavy Duty Diesel Mechanic',
    location: 'Nassau, New Providence',
    type: 'full-time',
    description: 'Skilled diesel mechanic needed to maintain and repair our fleet of heavy-duty trucks, equipment, and marine engines. ASE certification preferred.',
    requirements: [
      'ASE certification or equivalent (preferred)',
      '5+ years experience with heavy-duty diesel engines',
      'Experience with Caterpillar, Detroit, Cummins engines',
      'Knowledge of hydraulic and electrical systems',
      'Ability to diagnose and repair complex mechanical issues',
      'Valid driver license',
    ],
    responsibilities: [
      'Perform preventive maintenance on fleet vehicles and equipment',
      'Diagnose and repair diesel engines, transmissions, and systems',
      'Maintain accurate service records and documentation',
      'Order parts and manage shop inventory',
      'Conduct safety inspections',
      'Provide emergency repair services as needed',
    ],
    benefits: [
      'Competitive hourly rate',
      'Health insurance',
      'Tool allowance',
      'Ongoing training and certifications',
      'Paid time off',
    ],
  },
  {
    id: 'waste-driver',
    title: 'Commercial Waste Collection Driver',
    location: 'Nassau, New Providence',
    type: 'full-time',
    description: 'CDL driver needed to operate commercial waste collection vehicles. Must have clean driving record and commitment to safety.',
    requirements: [
      'Valid CDL Class B license',
      'Clean driving record (3+ years)',
      '2+ years experience operating commercial vehicles',
      'Ability to lift 50+ lbs repeatedly',
      'Strong customer service skills',
      'Flexibility for early morning shifts',
    ],
    responsibilities: [
      'Operate waste collection vehicles on assigned routes',
      'Collect commercial and industrial waste safely',
      'Conduct pre-trip and post-trip vehicle inspections',
      'Maintain route logs and documentation',
      'Provide excellent customer service',
      'Follow all safety protocols and regulations',
    ],
    benefits: [
      'Competitive hourly wage',
      'Health insurance after 90 days',
      'Paid time off',
      'Safety bonuses',
      'Overtime opportunities',
    ],
  },
  {
    id: 'logistics-coordinator',
    title: 'Logistics Coordinator',
    location: 'Nassau, New Providence',
    type: 'full-time',
    description: 'Organized professional needed to coordinate logistics operations, schedule deliveries, and manage customer communications for our multi-service operations.',
    requirements: [
      'Associate or Bachelor degree preferred',
      '2+ years experience in logistics or operations',
      'Strong organizational and multitasking abilities',
      'Proficiency in logistics software and MS Office',
      'Excellent communication skills',
      'Problem-solving mindset',
    ],
    responsibilities: [
      'Coordinate barge schedules and cargo manifests',
      'Schedule truck deliveries and waste collection routes',
      'Communicate with customers regarding shipments and services',
      'Track shipments and provide status updates',
      'Resolve logistics issues and delays',
      'Maintain accurate records and documentation',
    ],
    benefits: [
      'Competitive salary',
      'Health and dental insurance',
      'Paid time off and holidays',
      'Professional development',
      'Performance bonuses',
    ],
  },
  {
    id: 'safety-officer',
    title: 'Safety & Compliance Officer',
    location: 'Nassau, New Providence',
    type: 'full-time',
    description: 'Safety professional needed to oversee safety programs, conduct training, and ensure regulatory compliance across all operations.',
    requirements: [
      'Bachelor degree in Safety Management or related field',
      'OSHA certification or equivalent',
      '3+ years experience in safety management',
      'Knowledge of maritime and transportation safety regulations',
      'Strong training and presentation skills',
      'Attention to detail and documentation',
    ],
    responsibilities: [
      'Develop and implement safety programs and policies',
      'Conduct safety training for all employees',
      'Perform safety audits and inspections',
      'Investigate incidents and implement corrective actions',
      'Ensure compliance with all safety regulations',
      'Maintain safety records and reporting',
    ],
    benefits: [
      'Competitive salary',
      'Comprehensive health insurance',
      'Professional certifications and training',
      'Paid time off',
      'Retirement plan',
    ],
  },
];
